import { AnimatePresence, motion } from "motion/react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { useT } from "../i18n";
import { cn } from "./ui";

export interface ToastOptions {
  title: string;
  body?: string;
  tone?: "neutral" | "success" | "warn" | "error";
  /** If given, an Undo button appears and the toast stays longer. */
  onUndo?: () => unknown;
  durationMs?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
}

const ToastContext = createContext<{ toast: (o: ToastOptions) => void }>({ toast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);
  const t = useT();

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (o: ToastOptions) => {
      const id = ++counter.current;
      setItems((xs) => [...xs.slice(-3), { ...o, id }]);
      const ms = o.durationMs ?? (o.onUndo ? 7000 : 3500);
      window.setTimeout(() => dismiss(id), ms);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-20 md:bottom-6 z-[70] flex flex-col items-center gap-2 px-4" aria-live="polite">
        <AnimatePresence>
          {items.map((it) => (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8 }}
              className={cn(
                "pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl text-white",
                it.tone === "success" ? "bg-primary-dark" : it.tone === "warn" ? "bg-amber-600" : it.tone === "error" ? "bg-red-700" : "bg-ink",
              )}
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold leading-tight">{it.title}</p>
                {it.body ? <p className="text-sm opacity-85 truncate">{it.body}</p> : null}
              </div>
              {it.onUndo ? (
                <button
                  className="min-h-[40px] rounded-xl bg-white/15 px-3 text-sm font-bold hover:bg-white/25"
                  onClick={async () => {
                    dismiss(it.id);
                    await it.onUndo?.();
                  }}
                >
                  {t("common.undo")}
                </button>
              ) : null}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
